import { COLOR_SYSTEM, Flex, Static, Text, Button, Tag } from "components/atoms";
import { SubscriptionPayment } from "common/models/types";
import { formatKstDate, formatKrw } from "utils/subscriptionDisplay";

interface Props {
  payment: SubscriptionPayment;
}

const attemptTypeLabel = (attemptType: SubscriptionPayment["attemptType"]): string => {
  switch (attemptType) {
    case "INITIAL":
      return "최초";
    case "RECURRING":
      return "정기";
    case "RETRY":
      return "재시도";
    case "CHANGE_CARD":
      return "카드변경";
    default:
      return "";
  }
};

const statusInfo = (
  status: SubscriptionPayment["status"]
): { label: string; color: "Gray" | "Blue" | "Red" | "Green" } => {
  switch (status) {
    case "PAID":
      return { label: "결제완료", color: "Blue" };
    case "SCHEDULED":
      return { label: "결제예정", color: "Gray" };
    case "FAILED":
      return { label: "결제실패", color: "Red" };
    case "CANCELLED":
      return { label: "취소", color: "Gray" };
    case "REFUNDED":
      return { label: "환불", color: "Gray" };
    default:
      return { label: status, color: "Gray" };
  }
};

const SubscriptionPaymentCard: React.FC<Props> = ({ payment }) => {
  const handleReceiptClick = () => {
    if (payment.receiptUrl) {
      window.open(
        payment.receiptUrl,
        "_blank",
        "noopener,noreferrer,width=600,height=800"
      );
    }
  };

  const isCancelled = payment.status === "CANCELLED" || payment.status === "REFUNDED";
  const amountColor = isCancelled
    ? COLOR_SYSTEM.get("Gray")[500]
    : COLOR_SYSTEM.get("Blue")[400];
  const cardBgOpacity = isCancelled ? 0.6 : 1;
  const status = statusInfo(payment.status);

  // PAID면 paidAt, FAILED면 reservedAt, 그 외 createdAt
  const displayDate =
    payment.paidAt || payment.reservedAt || payment.createdAt;

  return (
    <Static
      customStyle={{
        width: "100%",
        background: COLOR_SYSTEM.get("Gray")[0],
        border: `1px solid ${COLOR_SYSTEM.get("Gray")[50]}`,
        borderRadius: "12px",
        overflow: "hidden",
        padding: "28px",
        marginBottom: "20px",
        opacity: cardBgOpacity,
      }}
    >
      <Flex
        gap="8px"
        customStyle={{
          width: "100%",
          marginBottom: "12px",
        }}
      >
        <Tag size="Small" variant="Contained" color="Gray">
          {attemptTypeLabel(payment.attemptType)}
        </Tag>
        <Tag size="Small" variant="Outlined" color={status.color}>
          {status.label}
        </Tag>
        <Text textType="Body2SemiBold" color={COLOR_SYSTEM.get("Gray")[500]}>
          {formatKstDate(displayDate, true)}
        </Text>
        <Static customStyle={{ flexGrow: 1 }} />
        {payment.receiptUrl && (
          <Button
            size="Small"
            type="Text"
            variant="Outlined"
            color="Secondary"
            onClick={handleReceiptClick}
          >
            영수증 보기
          </Button>
        )}
      </Flex>

      <Flex
        gap="12px"
        alignItems="center"
        customStyle={{
          width: "100%",
          marginBottom: "12px",
        }}
      >
        <Text textType="H4" color={COLOR_SYSTEM.get("Gray")[800]}>
          PhAI 유료 프리미엄
        </Text>
        {(payment.periodStart || payment.periodEnd) && (
          <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")[600]}>
            {formatKstDate(payment.periodStart)} ~ {formatKstDate(payment.periodEnd)}
          </Text>
        )}
        <Text textType="H4" color={amountColor}>
          {formatKrw(payment.amount)}
        </Text>
      </Flex>

      <Static
        customStyle={{
          padding: "20px 24px",
          width: "100%",
          borderRadius: "12px",
          border: `1px solid ${COLOR_SYSTEM.get("Gray")[50]}`,
        }}
      >
        <Flex alignItems="center" customStyle={{ marginBottom: "8px" }}>
          <Text
            textType="Body1Medium"
            color={COLOR_SYSTEM.get("Gray")[600]}
            style={{ width: "100px" }}
          >
            결제 번호
          </Text>
          <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")[800]}>
            {payment.impUid?.replace("imp_", "") || payment.merchantUid}
          </Text>
        </Flex>
        <Flex alignItems="center" customStyle={{ marginBottom: "8px" }}>
          <Text
            textType="Body1Medium"
            color={COLOR_SYSTEM.get("Gray")[600]}
            style={{ width: "100px" }}
          >
            결제 방법
          </Text>
          <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")[800]}>
            {payment.cardName || payment.payMethod || "-"}
          </Text>
        </Flex>
        {payment.applyNumber && (
          <Flex alignItems="center" customStyle={{ marginBottom: "8px" }}>
            <Text
              textType="Body1Medium"
              color={COLOR_SYSTEM.get("Gray")[600]}
              style={{ width: "100px" }}
            >
              승인 번호
            </Text>
            <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")[800]}>
              {payment.applyNumber}
            </Text>
          </Flex>
        )}
        {payment.status === "FAILED" && payment.failureReason && (
          <Flex alignItems="flex-start" customStyle={{ marginTop: "8px" }}>
            <Text
              textType="Body1Medium"
              color={COLOR_SYSTEM.get("Red")[400]}
              style={{ width: "100px" }}
            >
              실패 사유
            </Text>
            <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Red")[400]}>
              {payment.failureReason}
            </Text>
          </Flex>
        )}
        {isCancelled && payment.cancelledAmount > 0 && (
          <Flex alignItems="center" customStyle={{ marginTop: "8px" }}>
            <Text
              textType="Body1Medium"
              color={COLOR_SYSTEM.get("Gray")[600]}
              style={{ width: "100px" }}
            >
              취소 금액
            </Text>
            <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")[800]}>
              {formatKrw(payment.cancelledAmount)}
            </Text>
          </Flex>
        )}
      </Static>
    </Static>
  );
};

export default SubscriptionPaymentCard;
